"use client"

import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from "react"

import { Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react"
import Image from "next/image"

import { CloseButton } from "@/components/ui/close-button"
import { tv, type VariantProps } from "@/lib/utils/tv"

const sheet = tv({
  slots: {
    backdrop: "bg-scrim z-modal-backdrop ease-productive duration-moderate fixed inset-0 transition data-closed:opacity-0",
    frame: "",
    panel: "z-modal ease-expressive duration-slow fixed flex flex-col transition motion-reduce:transition-none",
    hero: "",
    header: "border-line-subtle flex items-center justify-between gap-300 border-b px-500 py-400",
    title: "font-display text-heading-sm text-fg font-bold",
    description: "text-body text-fg-heading",
    body: "min-h-0 flex-1 overflow-y-auto overscroll-contain p-500",
    footer: "border-line-subtle bg-surface-raised shrink-0 border-t px-500 pt-400 pb-safe-offset-400",
    handle: "bg-line mx-auto mt-200 h-1.5 w-25 shrink-0 rounded-full",
    close: "",
    grip: "",
    outsideHandle: "bg-fg-heading/50 absolute -top-4 left-1/2 h-1.5 w-25.25 -translate-x-1/2 rounded-full",
  },
  variants: {
    side: {
      bottom: { panel: "chrome-frost rounded-t-12x vv-bottom vv-max-h inset-x-0 data-closed:translate-y-full" },
      right: { panel: "chrome-frost inset-y-0 right-0 w-90 max-w-full data-closed:translate-x-full" },
      story: {
        backdrop: "bg-scrim-strong ease-settle duration-sheet-enter data-leave:duration-sheet-exit hidden motion-reduce:transition-none md:block",
        frame: "z-modal fixed inset-0 flex md:items-center md:justify-center md:p-600",
        panel:
          "group bg-story-referral-sheet pb-safe-offset-200 ease-settle duration-sheet-enter data-leave:duration-sheet-exit md:bg-surface md:rounded-12x md:shadow-panel md:max-w-story relative h-dvh w-full overflow-y-auto overscroll-contain data-closed:translate-y-full md:h-auto md:max-h-full md:bg-none md:pb-0 md:data-closed:translate-y-6 md:data-closed:scale-95 md:data-closed:opacity-0",
        hero: "pt-safe-offset-400 md:bg-story-referral-sheet relative flex shrink-0 grow flex-col px-500 pb-800 md:grow-0 md:px-800 md:pt-800 md:pb-400",
        grip: "bg-surface rounded-t-15x mx-200 flex h-6 shrink-0 cursor-grab touch-none items-center justify-center select-none active:cursor-grabbing md:hidden",
        handle: "bg-line-strong mt-0 h-1.25 w-9",
        body: "bg-surface rounded-b-15x relative mx-200 flex-none overflow-visible px-400 pt-0 pb-400 md:mx-0 md:rounded-none md:px-800 md:pt-200 md:pb-800",
      },
    },
    compact: {
      true: {
        backdrop: "bg-scrim-soft backdrop-blur-frost",
        panel: "rounded-t-7-5x shadow-panel",
        handle: "bg-line-strong mt-375 h-1.25 w-9",
        header: "border-0 px-375 pt-600 pb-300",
        title: "font-text text-little text-fg-heading font-medium",
        body: "px-375 pt-0 pb-500",
        close: "top-375 right-375 absolute",
      },
    },
    wide: { true: {} },
    // A searchable picker keeps its height while results narrow, so its top edge doesn't drop with each keystroke.
    tall: { true: { panel: "vv-h-floating" } },
    solid: { true: { panel: "bg-surface-raised backdrop-blur-none" } },
    inset: { gut: { body: "px-gut py-400" }, gutLoose: { body: "px-gut pt-400 pb-500" } },
    lift: { true: { backdrop: "bg-scrim-soft backdrop-blur-frost", panel: "rounded-t-7-5x shadow-panel" } },
    picker: {
      true: {
        backdrop: "bg-scrim-strong",
        // Closed, the panel also clears its floating gap, so no strip of it stays on screen while it leaves.
        panel:
          "onboarding-type bg-surface-flow rounded-15x shadow-panel vv-max-h-floating mb-floating data-closed:translate-y-floating-out max-w-onboarding inset-x-2 mx-auto backdrop-blur-none",
        handle: "bg-line-strong h-1.25 w-9",
        header: "justify-center border-0 pt-600 pb-400",
        title: "font-text text-center font-semibold",
        description: "text-body-lg tracking-snug text-fg text-center",
        body: "scrollbar-none scroll-fade-b mb-400 px-300 py-0",
        close: "sr-only",
        grip: "flex shrink-0 cursor-grab touch-none flex-col select-none active:cursor-grabbing",
      },
    },
    centered: {
      true: {
        panel: "lg:max-w-takeover lg:top-1/2 lg:bottom-auto lg:mb-0 lg:-translate-y-1/2 lg:data-closed:-translate-y-1/2 lg:data-closed:scale-95 lg:data-closed:opacity-0",
        handle: "lg:hidden",
      },
    },
    headline: {
      plain: {},
      onboarding: { header: "flex-col gap-400 pt-0 pb-500", title: "text-onboarding-title text-balance" },
      start: { header: "justify-start pt-0 pb-600", title: "text-flow-xl-semibold text-left" },
    },
  },
  compoundVariants: [
    { side: "right", wide: true, class: { panel: "w-180" } },
    { picker: true, headline: "plain", class: { title: "text-flow-xl-semibold leading-tight tracking-tight" } },
  ],
  defaultVariants: { side: "bottom", headline: "plain" },
})

const ENTERING_PRESS_MS = 1000
const DISMISS_FRACTION = 0.3
const FLICK_PX_PER_MS = 0.5
const FLICK_WINDOW_MS = 100

// Moves the panel with the pointer through inline styles, so releasing hands it back to the panel's own transition: springing home, or out from where it was dropped.
// A dismissed panel keeps its drag offset: Headless UI starts the leave with transitions off and a forced reflow, which would apply any new inline transform instantly.
const useDragToDismiss = (open: boolean, onDismiss: () => void) => {
  const panel = useRef<HTMLDivElement>(null)
  const drag = useRef<{ pointerId: number; startY: number; y: number; at: number; speed: number } | null>(null)

  useEffect(() => {
    if (!open) return
    drag.current = null
    panel.current?.style.removeProperty("transform")
    panel.current?.style.removeProperty("transition")
  }, [open])

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current || !event.isPrimary || event.button !== 0 || !panel.current) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { pointerId: event.pointerId, startY: event.clientY, y: event.clientY, at: performance.now(), speed: 0 }
    panel.current.style.transition = "none"
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const active = drag.current
    if (active?.pointerId !== event.pointerId || !panel.current) return
    const now = performance.now()
    active.speed = (event.clientY - active.y) / Math.max(1, now - active.at)
    active.y = event.clientY
    active.at = now
    panel.current.style.transform = `translateY(${Math.max(0, active.y - active.startY)}px)`
  }

  const release = (event: PointerEvent<HTMLDivElement>) => {
    const active = drag.current
    if (active?.pointerId !== event.pointerId || !panel.current) return
    drag.current = null
    const offset = Math.max(0, event.clientY - active.startY)
    const flicked = active.speed > FLICK_PX_PER_MS && performance.now() - active.at < FLICK_WINDOW_MS
    const dismiss = event.type === "pointerup" && offset > 0 && (flicked || offset > panel.current.getBoundingClientRect().height * DISMISS_FRACTION)
    panel.current.style.transition = ""
    if (dismiss) onDismiss()
    else panel.current.style.removeProperty("transform")
  }

  return { panel, grip: { onPointerDown, onPointerMove, onPointerUp: release, onPointerCancel: release } }
}

// Whether the body has more to scroll below, so the bottom fade shows only while there is.
const useMoreBelow = () => {
  const [body, setBody] = useState<HTMLDivElement | null>(null)
  const [more, setMore] = useState(false)

  useEffect(() => {
    if (!body) return
    const measure = () => setMore(body.scrollTop + body.clientHeight < body.scrollHeight - 1)
    measure()
    body.addEventListener("scroll", measure, { passive: true })
    const resize = new ResizeObserver(measure)
    resize.observe(body)
    if (body.firstElementChild) resize.observe(body.firstElementChild)
    return () => {
      body.removeEventListener("scroll", measure)
      resize.disconnect()
    }
  }, [body])

  return { body: setBody, more }
}

interface SheetProps extends VariantProps<typeof sheet> {
  open: boolean
  onClose: (open: boolean) => void
  label?: string
  testId?: string
  hero?: ReactNode
  art?: ReactNode
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  handle?: boolean | "outside"
  // False while the sheet's content must stay on screen: drag, backdrop and Escape leave it open.
  dismissible?: boolean
}

export const Sheet = ({
  open,
  onClose,
  side,
  wide,
  tall,
  solid,
  inset,
  lift,
  compact,
  picker,
  centered,
  headline,
  label,
  testId,
  hero,
  art,
  title,
  description,
  children,
  footer,
  handle = false,
  dismissible = true,
}: SheetProps) => {
  const styles = sheet({ side, wide, tall, solid, inset, lift, compact, picker, centered, headline })
  const story = side === "story"
  const dismiss = (next: boolean) => {
    if (dismissible) onClose(next)
  }
  const { panel, grip } = useDragToDismiss(open, () => onClose(false))
  const enteringPressAt = useRef(-Infinity)
  const { body, more } = useMoreBelow()
  const top = (
    <>
      {handle === true && <div className={styles.handle()} aria-hidden />}
      {title && (
        <div className={styles.header()}>
          {description ? (
            <div className="flex flex-col">
              <DialogTitle className={styles.title()}>{title}</DialogTitle>
              <Description className={styles.description()}>{description}</Description>
            </div>
          ) : (
            <>
              {art}
              <DialogTitle className={styles.title()}>{title}</DialogTitle>
            </>
          )}
          <div className={styles.close()}>
            <CloseButton
              onClick={() => dismiss(false)}
              className={compact ? "text-fg-brand size-6" : undefined}
              icon={compact ? <Image src="/images/icons/sort-close.svg" alt="" width={16} height={16} className="rotate-45" /> : undefined}
            />
          </div>
        </div>
      )}
    </>
  )
  const content = (
    <DialogPanel ref={panel} transition className={`${styles.panel()} ${footer || picker || story ? "" : "pb-safe"}`}>
      {handle === "outside" && <div className={styles.outsideHandle()} aria-hidden />}
      {hero && <div className={styles.hero()}>{hero}</div>}
      {picker || story ? (
        <div {...(dismissible && grip)} className={styles.grip()}>
          {top}
        </div>
      ) : (
        top
      )}
      <div ref={picker ? body : undefined} data-more={more || undefined} className={styles.body()}>
        {children}
      </div>
      {footer && <div className={styles.footer()}>{footer}</div>}
    </DialogPanel>
  )
  return (
    <Dialog
      open={open}
      onClose={(next) => {
        if (performance.now() - enteringPressAt.current > ENTERING_PRESS_MS) dismiss(next)
      }}
      aria-label={label}
      data-testid={testId}
    >
      <DialogBackdrop
        transition
        onPointerDown={() => {
          enteringPressAt.current = panel.current?.hasAttribute("data-enter") ? performance.now() : -Infinity
        }}
        className={styles.backdrop()}
      />
      {story ? <div className={styles.frame()}>{content}</div> : content}
    </Dialog>
  )
}
